import { ApiError } from '@discovr/common';
import { errorMessage } from './errorCopy';
import { editConfigFailure, shopifyEditCopy } from './shopifyEdit';

/** Why a live creator's code, or the campaign code while any creator is live, can't change. */
export const shopifyLiveLockCopy = {
  creator:
    'This creator is already live — their code and link can’t change while their content is live',
  campaign:
    'A creator on this campaign is already live — this shared code and its link can’t change while their content is live',
} as const;

/** Copy for changing a code's text in place ("Change code text"): same discount, new code. */
export const shopifyRenameCopy = {
  action: 'Change code text',
  title: 'Change code text',
  description: (scope: 'creator' | 'campaign') =>
    `Give ${scope === 'campaign' ? 'this campaign’s' : 'this creator’s'} discount a different code. The discount, its settings and past orders stay as they are.`,
  currentLabel: 'Current discount code',
  newLabel: 'New discount code',
  placeholder: 'SUMMER25',
  consequence: 'Creators who already shared the old code need the new one.',
  oldStops: 'The old code stops working at checkout as soon as you save.',
  submit: 'Change code text',
  submitting: 'Changing…',
  mustDiffer: 'Enter a code different from the current one.',
  // Toasts stay generic: they render outside the masked containers.
  renamed: 'Code text changed. Creators who already shared the old code need the new one.',
  errors: {
    generic: 'Couldn’t change the code text',
    codeTaken: 'That code is already used in your Shopify store.',
    valueInCode:
      'This code has its discount value built in (like SARAH15), so its text can’t be changed. To use a different code, create a new one.',
  },
} as const;

export interface RenameFailure {
  message: string;
  /** Shown beside the code field instead of as a toast. */
  field?: boolean;
  /** The row changed or vanished under the form; reopening it would show stale data. */
  closeDialog: boolean;
  refreshList: boolean;
}

export function renameFailure(err: unknown, scope: 'creator' | 'campaign'): RenameFailure {
  const copy = shopifyRenameCopy.errors;
  if (err instanceof ApiError) {
    if (err.code === 'SHOPIFY_CODE_TAKEN' && err.httpStatus === 409) {
      return { message: copy.codeTaken, field: true, closeDialog: false, refreshList: false };
    }
    if (err.code === 'SHOPIFY_VALUE_IN_CODE') {
      return { message: copy.valueInCode, closeDialog: false, refreshList: false };
    }
    if (err.code === 'SHOPIFY_CREATOR_LIVE') {
      return {
        message: shopifyLiveLockCopy[scope],
        closeDialog: false,
        refreshList: true,
      };
    }
    if (err.code === 'SHOPIFY_EDIT_STALE') {
      return { message: shopifyEditCopy.errors.editStale, closeDialog: true, refreshList: true };
    }
    const shared = editConfigFailure(err);
    if (shared.closeDialog || shared.refreshList) {
      return {
        message: shared.message,
        closeDialog: shared.closeDialog,
        refreshList: shared.refreshList,
      };
    }
  }
  return { message: errorMessage(err, copy.generic), closeDialog: false, refreshList: false };
}
